import { z } from 'zod';
import { ObjectIdSchema } from './object-id.schema';
/**
 * The `renderEntityView` tool contract (plan 016 §1): the product's own record
 * surfaces embedded in a transcript instead of paraphrased into markdown. ONE
 * tool with a `view` discriminator, because the AI SDK derives a message part
 * type from the tool name and a tool per view would pay the nine-touchpoint
 * part-registration path once per view.
 *
 * THE PAYLOAD IS IDS AND NOTHING ELSE. The renderer live-fetches through the
 * same authorized endpoints the detail pages use, so a view shows what its
 * reader may see AT VIEW TIME; a denormalized label is persisted in the part
 * and would keep naming a record whose reader has since lost access.
 */ /**
 * A UI-capability fact: the four chat entity types `@bliks-io/ui` ships an
 * `AssigneeCard` for. `department`, `process` and `artifact` have no card
 * component at all, which is why they can only ever be mentioned.
 *
 * Anything that draws a card narrows to this set — the history-only card arm
 * below is one such consumer, not the reason the set exists.
 */ const CHAT_ENTITY_CARD_TYPES = [
    'user',
    'system',
    'team',
    'externalContact'
];
const ChatEntityCardTypeSchema = z.enum(CHAT_ENTITY_CARD_TYPES);
const processIdField = ObjectIdSchema.describe('The process `_id`, resolved beforehand via lookupProcessDetails, ' + 'lookupProcesses or searchProcessKnowledge. Never invent one.');
const stepRecordingIdField = ObjectIdSchema.describe("The step recording's own `_id` within that process, as lookupProcessDetails " + 'returns it per step (or searchProcessKnowledge as `matchedStep._id`).');
/** One recorded step, drawn by the process page's own detail component. */ const ProcessStepViewSchema = z.object({
    view: z.literal('processStep'),
    processId: processIdField,
    stepRecordingId: stepRecordingIdField
});
/**
 * The same step's decision table alone — a view of its own because "which rules
 * decide where this step goes" is a different question, and the full detail
 * buries the table. It lives ON the step, so it needs no third id.
 */ const StepDecisionRulesViewSchema = z.object({
    view: z.literal('stepDecisionRules'),
    processId: processIdField,
    stepRecordingId: stepRecordingIdField
});
/**
 * A whole process as its published flow diagram. One id and nothing else: the
 * geometry is the process's own saved layout, fetched with it, so the chat
 * draws the same picture as the page one click away.
 */ const ProcessFlowViewSchema = z.object({
    view: z.literal('processFlow'),
    processId: processIdField
});
/**
 * HISTORY ONLY (plan 020 §S3). One person / system / team / external contact,
 * as transcripts recorded before the card retired still hold it. Nothing may
 * request this view; it exists so stored parts keep rendering.
 */ const EntityCardResultSchema = z.object({
    view: z.literal('entityCard'),
    entityType: ChatEntityCardTypeSchema,
    entityId: ObjectIdSchema
});
/**
 * The record to embed. A discriminated union rather than a wide object with
 * optional ids, so a `processStep` call missing its step id fails before
 * `execute` runs.
 */ const RenderEntityViewTargetSchema = z.discriminatedUnion('view', [
    ProcessStepViewSchema,
    StepDecisionRulesViewSchema,
    ProcessFlowViewSchema
]);
/**
 * The union NESTED under one property, never at the root: a tool's input schema
 * becomes the provider's function `parameters`, which must be a JSON Schema
 * OBJECT, and a top-level `z.discriminatedUnion` converts to a bare `oneOf`
 * with no root type — failing the WHOLE tool set on every turn. `renderChart`
 * nests under `query` for the same reason.
 */ export const RenderEntityViewInputSchema = z.object({
    target: RenderEntityViewTargetSchema.describe('The record to embed: its view plus exactly the ids that view needs.')
});
/**
 * The same reference echoed after validation and authorization, and FLAT where
 * the input is nested: `rendersEntityView` (`parts.ts`) reads `part.output.view`
 * on the RAW payload before any schema runs, so nesting would move that read to
 * `output.target.view` and bake the provider's wrapper into every stored row.
 *
 * SPREAD from the request union rather than re-listed, so "everything the model
 * may request is drawable" holds by construction: two hand-maintained lists
 * would let a fourth requestable view be added there and forgotten here, which
 * costs what `CHAT_ENTITY_VIEWS` describes below. The card is appended, not
 * spread — it is the one arm that exists only here.
 */ export const RenderEntityViewOutputSchema = z.discriminatedUnion('view', [
    ...RenderEntityViewTargetSchema.options,
    EntityCardResultSchema
]);
/**
 * Every view this contract knows, DERIVED from the union: `rendersEntityView`
 * tests membership here, so a view missing from a hand-maintained copy would
 * draw nothing AND — through `hasVisibleContent` — suppress the whole message
 * row, with no type error and no failing test. Frozen as well as `readonly`
 * because `.map()` hands back a mutable array and this is exported.
 */ export const CHAT_ENTITY_VIEWS = Object.freeze(RenderEntityViewOutputSchema.options.map((arm)=>arm.shape.view.value));
/**
 * The views the model may request — a subset of `CHAT_ENTITY_VIEWS` by
 * construction, since the output union spreads this one. The asymmetry is the
 * point (plan 020 §S3): `entityCard` retired as a request yet stays drawable
 * forever, because stored transcripts hold parts nobody can mint again.
 *
 * Everything that ADVERTISES the tool — its description, the Langfuse one, the
 * system prompt's own catalogue — is hand-written prose that cannot be derived
 * from an array. This constant is what
 * `apps/omniscient/chat/__tests__/tools/render-entity-view.test.ts` asserts each
 * in-repo copy against, in both directions, so a requestable view missing from
 * one and a retired view still named in another both fail.
 * Anything that RENDERS a stored part derives from `CHAT_ENTITY_VIEWS` instead.
 */ export const CHAT_REQUESTABLE_ENTITY_VIEWS = Object.freeze(RenderEntityViewTargetSchema.options.map((arm)=>arm.shape.view.value));

//# sourceMappingURL=entity-view.js.map